import { useEffect, useRef, useState } from 'react'
import type { Locale } from '../content/site'

const signImage = '/images/brand/paye-sign-original.jpg'

export function HeroMark({ locale }: { locale: Locale }) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [fallback, setFallback] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const image = new Image()
    let disposed = false
    let cleanup = () => {}

    image.onload = async () => {
      if (disposed) return

      try {
        const THREE = await import('three')
        if (disposed) return
        const source = document.createElement('canvas')
        const sourceWidth = 360
        const sourceHeight = 310
        source.width = sourceWidth
        source.height = sourceHeight
        const context = source.getContext('2d', { willReadFrequently: true })
        if (!context) throw new Error('Canvas is unavailable')

        // The supplied sign has a white mark on blue. Sample only its left-hand symbol.
        context.drawImage(image, image.width * .157, image.height * .368, image.width * .29, image.height * .254, 0, 0, sourceWidth, sourceHeight)
        const pixels = context.getImageData(0, 0, sourceWidth, sourceHeight).data
        const positions: number[] = []
        const colors: number[] = []
        const color = new THREE.Color()

        for (let y = 2; y < sourceHeight - 2; y += 2) {
          for (let x = 2; x < sourceWidth - 2; x += 2) {
            const index = (y * sourceWidth + x) * 4
            if (pixels[index] < 190 || pixels[index + 1] < 190 || Math.random() < .1) continue
            const jitter = (Math.random() - .5) * .035
            positions.push((x / sourceWidth - .5) * 5.45 + jitter, (.5 - y / sourceHeight) * 4.7 + jitter, (Math.random() - .5) * .24)
            color.set(Math.random() > .35 ? '#1243ed' : '#4168ff')
            colors.push(color.r, color.g, color.b)
          }
        }

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' })
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75))
        renderer.setClearColor(0x000000, 0)
        host.appendChild(renderer.domElement)
        renderer.domElement.setAttribute('aria-hidden', 'true')

        const scene = new THREE.Scene()
        const camera = new THREE.OrthographicCamera(-4, 4, 3, -3, .1, 30)
        camera.position.z = 10
        const mark = new THREE.Group()
        scene.add(mark)

        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
        const homePositions = new Float32Array(positions)
        const positionAttribute = geometry.getAttribute('position') as InstanceType<typeof THREE.BufferAttribute>
        const animatedPositions = positionAttribute.array as Float32Array
        const particlePhases = new Float32Array(positions.length / 3)
        for (let i = 0; i < particlePhases.length; i++) particlePhases[i] = Math.random() * Math.PI * 2
        const dotCanvas = document.createElement('canvas')
        dotCanvas.width = dotCanvas.height = 32
        const dotContext = dotCanvas.getContext('2d')
        if (!dotContext) throw new Error('Canvas is unavailable')
        dotContext.fillStyle = '#fff'
        dotContext.beginPath()
        dotContext.arc(16, 16, 15, 0, Math.PI * 2)
        dotContext.fill()
        const dotTexture = new THREE.CanvasTexture(dotCanvas)
        const particleMaterial = new THREE.PointsMaterial({ size: 2.15, sizeAttenuation: false, map: dotTexture, transparent: true, alphaTest: .3, vertexColors: true, depthWrite: false })
        const particleMark = new THREE.Points(geometry, particleMaterial)
        mark.add(particleMark)

        const haloGeometry = new THREE.BufferGeometry()
        const haloPositions: number[] = []
        for (let i = 0; i < 650; i++) {
          haloPositions.push((Math.random() - .5) * 8.5, (Math.random() - .5) * 6.5, -1.2)
        }
        haloGeometry.setAttribute('position', new THREE.Float32BufferAttribute(haloPositions, 3))
        const haloMaterial = new THREE.PointsMaterial({ color: '#6389ff', size: 1.1, sizeAttenuation: false, map: dotTexture, transparent: true, opacity: .28, depthWrite: false })
        scene.add(new THREE.Points(haloGeometry, haloMaterial))

        let frame = 0
        let visible = true
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const pointer = { x: 0, y: 0, worldX: 0, worldY: 0, active: false, strength: 0 }
        const onPointerMove = (event: PointerEvent) => {
          const bounds = host.getBoundingClientRect()
          pointer.x = (event.clientX - bounds.left) / bounds.width - .5
          pointer.y = (event.clientY - bounds.top) / bounds.height - .5
          pointer.worldX = pointer.x * (camera.right - camera.left)
          pointer.worldY = -pointer.y * (camera.top - camera.bottom)
          pointer.active = true
        }
        const onPointerLeave = () => { pointer.x = 0; pointer.y = 0; pointer.active = false }
        host.addEventListener('pointermove', onPointerMove)
        host.addEventListener('pointerleave', onPointerLeave)

        const resize = () => {
          const width = Math.max(host.clientWidth, 1)
          const height = Math.max(host.clientHeight, 1)
          const aspect = width / height
          const worldHeight = aspect < .8 ? 7.2 : 6
          camera.left = -worldHeight * aspect / 2
          camera.right = worldHeight * aspect / 2
          camera.top = worldHeight / 2
          camera.bottom = -worldHeight / 2
          camera.updateProjectionMatrix()
          renderer.setSize(width, height, false)
          renderer.render(scene, camera)
        }
        const observer = new ResizeObserver(resize)
        observer.observe(host)
        const intersection = new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting
          if (visible && !reducedMotion && !frame) frame = requestAnimationFrame(tick)
        })
        intersection.observe(host)

        const start = performance.now()
        let previousFrame = start
        function tick(now: number) {
          frame = 0
          if (!visible) return
          const time = (now - start) * .001
          const frameScale = Math.min((now - previousFrame) / 16.67, 2)
          previousFrame = now
          const follow = 1 - Math.exp(-.13 * frameScale)
          pointer.strength += ((pointer.active ? 1 : 0) - pointer.strength) * (1 - Math.exp(-.1 * frameScale))

          // A small drifting grain stays alive at rest. The pointer pushes nearby
          // particles aside; their spring return restores the exact source mark.
          for (let i = 0; i < particlePhases.length; i++) {
            const offset = i * 3
            const homeX = homePositions[offset]
            const homeY = homePositions[offset + 1]
            const phase = particlePhases[i]
            let targetX = homeX + Math.sin(time * 1.35 + phase) * .012
            let targetY = homeY + Math.cos(time * 1.1 + phase) * .012
            let targetZ = homePositions[offset + 2] + Math.sin(time * 1.5 + phase) * .025

            if (pointer.strength > .005) {
              const dx = homeX - pointer.worldX
              const dy = homeY - pointer.worldY
              const distance = Math.hypot(dx, dy)
              const radius = 1.12
              if (distance < radius) {
                const falloff = (1 - distance / radius) ** 2 * pointer.strength
                const inverseDistance = 1 / Math.max(distance, .06)
                targetX += (dx * .43 - dy * .11) * inverseDistance * falloff
                targetY += (dy * .43 + dx * .11) * inverseDistance * falloff
                targetZ += .24 * falloff
              }
            }

            animatedPositions[offset] += (targetX - animatedPositions[offset]) * follow
            animatedPositions[offset + 1] += (targetY - animatedPositions[offset + 1]) * follow
            animatedPositions[offset + 2] += (targetZ - animatedPositions[offset + 2]) * follow
          }
          positionAttribute.needsUpdate = true
          mark.rotation.y += ((pointer.x * .16) - mark.rotation.y) * .04
          mark.rotation.x += ((-pointer.y * .1) - mark.rotation.x) * .04
          mark.position.y = Math.sin(time * .8) * .075
          renderer.render(scene, camera)
          frame = requestAnimationFrame(tick)
        }

        resize()
        if (!reducedMotion) frame = requestAnimationFrame(tick)
        cleanup = () => {
          cancelAnimationFrame(frame)
          observer.disconnect()
          intersection.disconnect()
          host.removeEventListener('pointermove', onPointerMove)
          host.removeEventListener('pointerleave', onPointerLeave)
          geometry.dispose()
          haloGeometry.dispose()
          particleMaterial.dispose()
          haloMaterial.dispose()
          dotTexture.dispose()
          renderer.dispose()
          renderer.domElement.remove()
        }
      } catch {
        setFallback(true)
      }
    }
    image.onerror = () => { if (!disposed) setFallback(true) }
    image.src = signImage

    return () => {
      disposed = true
      image.onload = null
      image.onerror = null
      cleanup()
    }
  }, [])

  return (
    <div className="hero-mark" role="img" aria-label={locale === 'fa' ? 'نشان متحرک پایه‌تک' : 'Animated Paye Tech mark'}>
      <div className="hero-mark__glow" aria-hidden="true" />
      <div className="hero-mark__canvas" ref={hostRef} />
      {fallback && <img className="hero-mark__fallback" src={signImage} alt="" />}
    </div>
  )
}
